// src/web/client/src/pages/AuthSuccess.jsx
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle } from 'lucide-react';

// Where to land after a successful OAuth — context-aware:
//
//   EMBEDDED (inside the admin iframe): breaking out to admin.shopify.com
//   is blocked by X-Frame-Options — the loop that dead-ended fresh installs
//   (and #160's variant) — and unnecessary: this SPA is already running on
//   the registered origin with App Bridge alive from the embedded URL
//   params, so an internal navigate('/dashboard') is the correct
//   continuation. → null.
//
//   TOP-LEVEL (App Store install running outside the admin): the admin
//   deep link — the billing-callback pattern — so the admin loads the app
//   on the registered origin with fresh embedded params. Unchanged.
//
//   Demo / non-myshopify domains stay on the local SPA exactly as before.
// Pure + exported for tests (isEmbedded computed at the call site).
export function postAuthDestination(shopDomain, isDemo, isEmbedded) {
  if (isEmbedded) return null; // in-iframe → internal navigate, never X-Frame
  if (!isDemo && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shopDomain || '')) {
    return `https://${shopDomain}/admin/apps/ecomoria-ai/dashboard`;
  }
  return null; // stay on this origin → internal navigate('/dashboard')
}

export default function AuthSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get parameters from URL
    const token = searchParams.get('token');
    const shopId = searchParams.get('shop_id');
    const shopDomain = searchParams.get('shop_domain') || searchParams.get('shop');

    if (token && shopId && shopDomain) {
      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('shop_id', shopId);
      localStorage.setItem('shopId', shopId);
      localStorage.setItem('shop_domain', shopDomain);
      localStorage.setItem('shopDomain', shopDomain);
      // (shop_name / shopName are no longer stored: the store's name is read
      // from the server — hooks/useShopProfile — so there is one source.)
      localStorage.setItem('onboarding_completed', 'true');

      // Close window if it was opened as popup
      if (window.opener) {
        try {
          window.opener.postMessage({ type: 'AUTH_SUCCESS', token }, '*');
        } catch (e) {
          console.error('Could not post message to opener:', e);
        }
        setTimeout(() => {
          window.close();
        }, 2000);
        return;
      }

      // Redirect back into the Shopify admin (registered origin) — or, when
      // already embedded / demo, an internal navigate to the dashboard.
      // window.top !== window.self is a safe identity comparison even when
      // the parent is cross-origin (property READS on window.top throw;
      // comparing the references does not).
      const isEmbedded = window.top !== window.self;
      const adminDeepLink = postAuthDestination(shopDomain, searchParams.get('demo') === 'true', isEmbedded);
      setTimeout(() => {
        if (adminDeepLink) {
          window.location.replace(adminDeepLink);
        } else {
          navigate('/dashboard');
        }
      }, 1500);
    } else {
      // Missing parameters - redirect to onboarding
      console.error('Missing authentication parameters');
      navigate('/onboarding');
    }
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Authentication Successful!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">
            Your Shopify store has been connected successfully.
          </p>
          <div className="flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gray-500 mr-2" />
            <span className="text-gray-500">Redirecting to dashboard...</span>
          </div>
          {window.opener && (
            <p className="text-sm text-gray-500">
              You can close this window if it doesn't close automatically.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}